import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";

const BRAND_OPTIONS = [
  "WhichSchoolAdvisor",
  "SchoolsCompared",
  "Top Schools Awards",
  "EdinformaPlus",
  "Education Intelligence Group",
  "WhichUniversityAdvisor",
];

const Newsletter = ({ dark = false }) => {
  const [email, setEmail] = useState("");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [mathAns, setMathAns] = useState("");

  const challenge = useMemo(() => ({
    a: Math.floor(Math.random() * 8) + 2,
    b: Math.floor(Math.random() * 8) + 2,
  }), []);

  const toggle = (b) =>
    setBrands((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email address.");
    if (brands.length === 0) return toast.error("Select at least one publication.");
    if (mathAns === "" || Number(mathAns) !== challenge.a + challenge.b) {
      return toast.error("Please answer the security question correctly.");
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("brands", brands.join(","));
      fd.append("website", website);
      fd.append("math_a", String(challenge.a));
      fd.append("math_b", String(challenge.b));
      fd.append("math_answer", String(mathAns));
      await api.post("/newsletter", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("You're subscribed. Welcome to Which Media.");
      setEmail("");
      setBrands([]);
      setMathAns("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  const label = dark ? "text-white/70" : "text-foreground/70";
  const inputText = dark ? "text-white placeholder:text-white/40" : "text-foreground placeholder:text-foreground/40";
  const borderCls = dark ? "border-white/30" : "border-foreground/40";

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="w-full max-w-xl">
      <p className={`kicker mb-2 ${dark ? "!text-primary" : ""}`}>The Briefing — Newsletter</p>
      <p className={`headline text-2xl md:text-3xl mb-6 ${dark ? "text-white" : "text-ink"}`}>
        Insight from across the portfolio, in your inbox.
      </p>
      <div className={`flex items-end gap-4 border-b ${borderCls} pb-3 focus-within:border-primary transition-colors`}>
        <input
          data-testid="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className={`flex-1 bg-transparent outline-none text-lg font-sans ${inputText}`}
        />
        <button
          data-testid="newsletter-submit"
          type="submit"
          disabled={loading}
          className="text-primary hover:translate-x-1 transition-transform disabled:opacity-40"
          aria-label="Subscribe"
        >
          <ArrowRight size={24} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
        {BRAND_OPTIONS.map((b) => (
          <label
            key={b}
            data-testid={`newsletter-brand-${b.replace(/\s+/g, "-").toLowerCase()}`}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <Checkbox
              checked={brands.includes(b)}
              onCheckedChange={() => toggle(b)}
              className={`${dark ? "border-white/40" : "border-foreground/30"} data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground`}
            />
            <span className={`text-sm group-hover:text-primary transition-colors ${label}`}>{b}</span>
          </label>
        ))}
      </div>

      {/* Honeypot — invisible to real users */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="absolute -left-[9999px] w-px h-px opacity-0"
      />

      {/* Math CAPTCHA */}
      <div className={`mt-6 flex items-center gap-3 text-sm ${label}`}>
        <label htmlFor="nl-math" className="font-sans font-semibold shrink-0">
          Security check: what is {challenge.a} + {challenge.b}?
        </label>
        <input
          id="nl-math"
          data-testid="newsletter-math"
          type="number"
          inputMode="numeric"
          value={mathAns}
          onChange={(e) => setMathAns(e.target.value)}
          placeholder="?"
          className={`max-w-[90px] bg-transparent border-b ${borderCls} focus:border-primary outline-none py-1 px-2 font-sans font-semibold ${inputText}`}
        />
      </div>
    </form>
  );
};

export default Newsletter;
